import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AsyncTable from '../../components/table/async';
import { SortTypeEnum } from '../../types';
import { mapPartnerFilters as mapFilters, mapPartnerSort as mapSort } from '../report-management/utils';
import { PartnerReviewListItem, PartnerReviewRequestType } from '../../types/partnerReport';
import { partnerReportService } from '../../lib/api/partnerReport';
import ReviewSidebar from '../report-management/reviewSidebar';
import { PartnerReportedActionRequestType, PartnerReportedDetailsRequestType } from '../../types/report';

const PartnerReviewReport: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<PartnerReviewListItem[]>([]);
    const [start, setStart] = useState(0);
    const [limit, setLimit] = useState(10);
    const [filters, setFilters] = useState<{ [key: string]: string } | null>(null);
    const [sort, setSort] = useState<{ [key: string]: SortTypeEnum } | null>({
        'ReportedDate.SortType': 2,
    });
    const [clearServerSearch, setClearServerSearch] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [show, setShow] = useState(false);
    const [reportedReviewData, setReportedReviewData] = useState<PartnerReportedDetailsRequestType | null>(null);
    const [actionData, setActionData] = useState<PartnerReportedActionRequestType | null>(null);
    const [suspendReasonText, setSuspendReasonText] = useState('');

    const handleClose = () => setShow(false);

    const setReviewTypeStatus = (reviewType: number) => {
        switch (reviewType) {
            case 0:
                return 'Partner Review';
            case 1:
                return 'Other Review';
            default:
                return '';
        }
    };

    const handleShow = (data: any, actionDataType: any) => {
        setShow(true);
        setReportedReviewData(data);
        if (actionDataType) {
            setActionData({
                offerId: actionDataType?.offerId,
                currentStatus: actionDataType?.currentStatus || actionDataType?.reportStatus,
                replyId: actionDataType?.reviewCommentId || actionDataType?.partnerReplyId || '',
                reportReplyId: actionDataType?.reportReplyId || '',
                reportReviewId: actionDataType?.reportReviewId,
                reviewId: actionDataType?.reviewId,
                reviewReplyText: actionDataType?.replyText || actionDataType?.reviewText || '',
                reviewType: actionDataType?.reviewType,
                suspendStatus: actionDataType?.suspendStatus || 0,
            });
        }
    };

    const setStatusMessage = (currentStatus: number) => {
        switch (currentStatus) {
            case 0:
                return 'Not Flagged';

            case 1:
                return 'Pending';

            case 2:
                return 'Deleted';

            case 3:
                return 'Retained';

            default:
                return '';
        }
    };

    const columns = useMemo(
        () => [
            {
                dataIndex: 'reportedByCompanyName',
                title: 'Reported By',
                render: (item: any) => (
                    <div className="text-crop">
                        <div className="big-text" title={item.reportedByCompanyName}>
                            {item.reportedByCompanyName}
                        </div>
                    </div>
                ),
            },
            {
                dataIndex: 'reportedOnCompanyName',
                title: 'Reported On',
                render: (item: any) => (
                    <div className="text-crop">
                        <div className="big-text" title={item.reportedOnCompanyName}>
                            {item.reportedOnCompanyName}
                        </div>
                    </div>
                ),
            },
            // {
            //     dataIndex: 'reviewType',
            //     title: 'Review Type',
            //     render: (item: any) => <div>{setReviewTypeStatus(item.reviewType)}</div>,
            // },
            {
                dataIndex: 'reportedDate',
                title: 'Reported Date',
                sorter: true,
                render: (item: any) => {
                    if (!item.reportedDate) return '';
                    return new Date(item.reportedDate).toLocaleString('en-in', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                    });
                },
            },

            {
                dataIndex: 'reportStatus',
                title: 'Status',
                render: (item: any) => <div>{setStatusMessage(item.reportStatus)}</div>,
            },
            {
                dataIndex: '',
                title: 'Actions',
                render: (item: any) => (
                    <div className="text-center">
                        <button
                            className=""
                            type="button"
                            onClick={() => {
                                handleShow({ reportReviewType: item.reviewType, reviewId: item.reviewId }, item);
                                setSuspendReasonText('item?.suspendedReasons');
                            }}
                        >
                            <i className="bi bi-eye"></i>
                        </button>
                    </div>
                ),
            },
        ],
        [],
    );

    const getDynamicSortField = (sortObject: { [key: string]: SortTypeEnum } | null): string => {
        if (!sortObject || Object.keys(sortObject).length === 0) {
            return 'ReportedDate';
        }
        const sortKey = Object.keys(sortObject)[0];

        switch (sortKey) {
            case 'ReportedByCompanyName.SortType':
                return 'ReportedByCompanyName';

            case 'ReportedOnCompanyName.SortType':
                return 'ReportedOnCompanyName';

            case 'ContractComplianceState.SortType':
                return 'ContractComplianceState';

            case 'ReviewType.SortType':
                return 'ReviewType';

            case 'ReportedDate.SortType':
                return 'ReportedDate';

            case 'ReportStatus.SortType':
                return 'ReportStatus';

            case 'ReportedById.SortType':
                return 'ReportedById';

            case 'ReportedByName.SortType':
                return 'ReportedByName';

            default:
                return 'ReportedDate';
        }
    };

    const getList = useCallback(async () => {
        try {
            !loading && setLoading(true);
            const dynamicSortField = getDynamicSortField(sort);

            const params: PartnerReviewRequestType = {
                Start: start,
                Limit: limit,
                SortField: dynamicSortField,
                ...filters,
                ...sort,
            };

            const {
                resultObject: { items, count },
            } = await partnerReportService.getPartnerReviewsList(params);

            setData(items);
            setTotalCount(count);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [start, limit, filters, sort]);

    useEffect(() => {
        setFilters(mapFilters([]));
        sessionStorage.clear();
        setClearServerSearch(true);
    }, []);

    useEffect(() => {
        (async function () {
            await getList();
        })();
    }, [start, limit, filters, sort]);

    return (
        <>
            <button
                onClick={() => {
                    setFilters(mapFilters([]));
                    sessionStorage.clear();
                    setClearServerSearch(true);
                }}
                className="btn btn-outline-primary btn-sm me-2 sticky-left"
                disabled={!data || !filters || !Object.values(filters).some((filter) => filter)}
            >
                <i className="bi bi-funnel"></i> Clear All
            </button>

            <div className="company-page-contener employer-table">
                <AsyncTable
                    loading={loading}
                    columns={columns}
                    data={data}
                    start={start}
                    setStart={setStart}
                    pageSize={limit}
                    clearServerSearch={clearServerSearch}
                    setClearServerSearch={setClearServerSearch}
                    setPageSize={setLimit}
                    totalCount={totalCount}
                    makeSearchRequest={(filters) => setFilters(mapFilters(filters))}
                    makeSortRequest={(value) => setSort(mapSort(value))}
                    isPartnerReview={true}
                />
            </div>
            <div className="review-sidebar">
                <ReviewSidebar
                    type="reviewReport"
                    handleClose={handleClose}
                    show={show}
                    reportedData={reportedReviewData}
                    actionData={actionData}
                    suspendReasonText={suspendReasonText}
                    getList={getList}
                    isPartner={true}
                />
            </div>
        </>
    );
};

export default PartnerReviewReport;
